// HeatmapWidget: renders the `risk-heatmap` envelope using @nivo/heatmap.
//
// The contract shape we accept:
//   { rows: string[], columns: string[], cells: Array<{ row, col, value, label? }> }
// Nivo wants `data: Array<{ id, data: Array<{ x, y }> }>`. We pivot.

import { ResponsiveHeatMap } from "@nivo/heatmap";
import { useMemo } from "react";

interface HeatCell {
  row?: string;
  col?: string;
  value?: number;
  label?: string;
}

interface HeatmapPayload {
  rows?: string[];
  columns?: string[];
  cells?: HeatCell[];
}

interface Props {
  data: unknown;
  // Called when the user clicks a cell. Receives the row and column labels
  // exactly as supplied in `data.rows` / `data.columns`.
  onCellClick?: (row: string, col: string, value: number) => void;
}

export function HeatmapWidget({ data, onCellClick }: Props) {
  const { nivoData, hasData } = useMemo(() => {
    const payload = (data ?? {}) as HeatmapPayload;
    const rows = payload.rows ?? [];
    const columns = payload.columns ?? [];
    const cells = payload.cells ?? [];
    if (!rows.length || !columns.length) {
      return { nivoData: [], hasData: false };
    }
    const byCell = new Map<string, number>();
    for (const c of cells) {
      if (c.row == null || c.col == null) continue;
      byCell.set(`${c.row}::${c.col}`, Number(c.value ?? 0));
    }
    const nivoData = rows.map((r) => ({
      id: r,
      data: columns.map((c) => ({
        x: c,
        y: byCell.get(`${r}::${c}`) ?? 0,
      })),
    }));
    return { nivoData, hasData: true };
  }, [data]);

  if (!hasData) {
    return (
      <div className="p-6 text-sm text-slate-500">
        No heatmap cells to render. Expecting <code>rows</code>, <code>columns</code>,
        and a <code>cells</code> array of {"{row, col, value}"} triples.
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-white">
      <ResponsiveHeatMap
        data={nivoData}
        // Generous top + left so rotated labels don't clip off the canvas.
        margin={{ top: 50, right: 32, bottom: 16, left: 96 }}
        // Cell values are integer counts — Nivo's SI default (1.0, 5.0)
        // looks like a measurement, not a count. Format as plain integer.
        valueFormat=",d"
        axisTop={{ tickRotation: 0, legend: "Impact", legendPosition: "middle", legendOffset: -36 }}
        axisLeft={{ tickRotation: 0, legend: "Probability", legendPosition: "middle", legendOffset: -76 }}
        axisRight={null}
        axisBottom={null}
        colors={{
          type: "sequential",
          scheme: "yellow_orange_red",
          minValue: 0,
        }}
        emptyColor="#f1f5f9"
        labelTextColor="#0f172a"
        onClick={onCellClick
          ? (cell) => {
              // Nivo's cell carries the row id under `serieId` and the column
              // under `data.x`. Forward both raw labels so callers can apply
              // a filter without re-parsing the heatmap data.
              const row = String((cell as any).serieId ?? "");
              const col = String((cell as any).data?.x ?? "");
              const value = Number((cell as any).data?.y ?? 0);
              if (row && col) onCellClick(row, col, value);
            }
          : undefined}
      />
    </div>
  );
}
